import React, { useEffect, useMemo, useState, useRef } from 'react';
import StarMapGenerator from '../starmap/StarMapGenerator';

// Import cities data to look up city names
import { fallbackCitiesData } from '../../utils/cityData';

// Shared style mapping function
const mapStyleName = (style) => {
    // Handle null/undefined styles
    if (!style) return 'stellar simplicity';

    // Convert to lowercase and handle spaces for comparison
    const normalizedStyle = String(style).toLowerCase().trim();

    // List of valid style names
    const validStyles = [
        'stellar simplicity',
        'orbit',
        'constellation frame',
        'celestial corners',
        'starlit halo',
        'lunar harmony',
        'astro mechanics',
        'cosmic crown'
    ];

    // Find a matching style (case insensitive)
    const matchedStyle = validStyles.find(valid =>
        valid.toLowerCase() === normalizedStyle
    );

    if (matchedStyle) {
        return matchedStyle;
    }

    // Default to stellar simplicity if no match
    console.log(`Style "${style}" not recognized, defaulting to stellar simplicity`);
    return 'stellar simplicity';
};

// SVG for the decorative orbit elements that surround the map
const OrbitalDecorations = ({ style = 'stellar simplicity' }) => {
    // Get the normalized style for SVG selection
    const normalizedStyle = mapStyleName(style);

    // Debug log to help diagnose issues (reduced to prevent spam)
    // console.log(`OrbitalDecorations - Using style: "${normalizedStyle}"`);

    // Choose the SVG source path based on style
    const getSvgPath = (style) => {
        switch (style) {
            case 'stellar simplicity': return process.env.PUBLIC_URL + '/assets/images/Design1_white.svg';
            case 'constellation frame': return process.env.PUBLIC_URL + '/assets/images/Design3_white.svg';
            case 'celestial corners': return process.env.PUBLIC_URL + '/assets/images/Design4_white.svg';
            case 'starlit halo': return process.env.PUBLIC_URL + '/assets/images/Design5_white.svg';
            case 'lunar harmony': return process.env.PUBLIC_URL + '/assets/images/Design6_white.svg';
            case 'astro mechanics': return process.env.PUBLIC_URL + '/assets/images/Design7_white.svg';
            case 'cosmic crown': return process.env.PUBLIC_URL + '/assets/images/Design8_white.svg';
            case 'orbit': return process.env.PUBLIC_URL + '/assets/images/Design2_white.svg';
            default: return process.env.PUBLIC_URL + '/assets/images/Design1_white.svg';
        }
    };

    // Get style-specific CSS adjustments for the <img> element
    const getStyleAdjustments = (style) => {
        switch (style) {
            case 'stellar simplicity':
                return { transform: 'scale(1)' };
            case 'orbit':
                return { transform: 'scale(1)', objectFit: 'fill' };
            case 'constellation frame':
                return { transform: 'scale(1)' };
            case 'celestial corners':
                return { transform: 'scale(1)' };
            case 'starlit halo':
                return { transform: 'scale(1)' };
            case 'lunar harmony':
                return { transform: 'scale(1)' };
            case 'astro mechanics':
                return { transform: 'scale(1)' };
            case 'cosmic crown':
                return { transform: 'scale(1)', objectFit: 'fill', padding: '0' };
            default:
                return { transform: 'scale(1)' };
        }
    };

    const styleAdjustments = getStyleAdjustments(normalizedStyle);

    return (
        <img
            src={getSvgPath(normalizedStyle)}
            className="w-full h-full"
            style={styleAdjustments}
            alt="Orbital decoration"
        />
    );
};

// Component to render the caption text below the map
const MapCaption = ({ caption, location, date, formData, cityName }) => {
    // Format the date
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    // Format coordinates for display
    const formatCoordinates = (locationCoords) => {
        if (!locationCoords) return '';

        try {
            const [lat, lng] = locationCoords.split(',').map(coord => parseFloat(coord.trim()));
            const latStr = `${Math.abs(lat).toFixed(2)}°${lat >= 0 ? 'N' : 'S'}`;
            const lngStr = `${Math.abs(lng).toFixed(2)}°${lng >= 0 ? 'E' : 'W'}`;
            return `${latStr}, ${lngStr}`;
        } catch (error) {
            return '';
        }
    };

    // Get city name from coordinates if cityName is not available
    const getCityNameFromCoordinates = (locationCoords) => {
        if (!locationCoords) return 'Location';

        try {
            // Parse coordinates
            const [lat, lng] = locationCoords.split(',').map(coord => parseFloat(coord.trim()));

            // Find matching city in fallback data (for common cities)
            const city = fallbackCitiesData.find(city =>
                Math.abs(city.lat - lat) < 0.1 && Math.abs(city.lng - lng) < 0.1
            );

            if (city) {
                return city.name;
            }

            // If no city found, return just 'Location'
            return 'Location';
        } catch (error) {
            return 'Location';
        }
    };

    // Determine what to show for location
    const getLocationDisplay = () => {
        if (formData.location) {
            // User has entered a location
            const displayCityName = formData.locationName || cityName || getCityNameFromCoordinates(location);

            if (displayCityName && displayCityName !== 'Location') {
                // Show city name only for cleaner display
                return displayCityName;
            } else {
                // Fallback to coordinates if no city name available
                const coordinates = formatCoordinates(location);
                return coordinates || 'Location';
            }
        } else {
            // No location entered, show placeholder
            return 'Location (e.g. London, UK)';
        }
    };

    // Determine what to show for date
    const getDateDisplay = () => {
        if (formData.date) {
            // User has entered a date, show the actual formatted date
            return formatDate(date);
        } else {
            // No date entered, show placeholder with today's date as example
            const today = new Date();
            const todayFormatted = formatDate(today.toISOString().split('T')[0]);
            return `Date (e.g. ${todayFormatted})`;
        }
    };

    return (
        <div className="text-center mt-4 mb-2 text-white/80" style={{ marginTop: '20px' }}>
            <div className="font-light text-lg mb-1">{caption || 'Special Text'}</div>
            <div className="text-xs opacity-70">
                {getLocationDisplay()}<br />
                {getDateDisplay()}
            </div>
        </div>
    );
};

// Background styles based on the selection
const getBackgroundStyle = (backgroundType) => {
    if (!backgroundType) return {};

    // Map background types to image files in the product-gradients directory
    const backgrounds = {
        // Classic Backgrounds (Free) - now using PNG files
        'gray': 'url("/images/product-gradients/classic-gray.png")',
        'black': 'url("/images/product-gradients/classic-black.png")',

        // Basic Harmony
        'basic-light': 'url("/images/product-gradients/harmony-1.png")',
        'basic-dark': 'url("/images/product-gradients/harmony-2.png")',

        // Sunset Vibe
        'sunset-pink': 'url("/images/product-gradients/sunset-1.png")',
        'sunset-orange': 'url("/images/product-gradients/sunset-2.png")',

        // Pastel Palette
        'pastel-pink': 'url("/images/product-gradients/pastel-1.png")',
        'pastel-blue': 'url("/images/product-gradients/pastel-2.png")',
        'pastel-white': 'url("/images/product-gradients/pastel-3.png")',

        // Neon Glow
        'neon-purple': 'url("/images/product-gradients/neon-1.png")',
        'neon-blue': 'url("/images/product-gradients/neon-2.png")',

        // Natural Tones
        'natural-gold': 'url("/images/product-gradients/natural-1.png")',
        'natural-brown': 'url("/images/product-gradients/natural-2.png")',
        'natural-beige': 'url("/images/product-gradients/natural-3.png")',

        // Golden Elegance
        'golden-dark': 'url("/images/product-gradients/golden-elegance-1.png")',
        'golden-black': 'url("/images/product-gradients/golden-elegance-2.png")',
        'golden-gold': 'url("/images/product-gradients/golden-elegance-3.png")'
    };

    const bgValue = backgrounds[backgroundType] || backgrounds['black'];

    // Use specific properties instead of shorthand to avoid conflicts
    // For colors, use backgroundColor; for images, use backgroundImage
    if (bgValue.startsWith('url(')) {
        return {
            backgroundImage: bgValue,
            width: '100%',
            height: '100%'
        };
    } else {
        return {
            backgroundColor: bgValue,
            width: '100%',
            height: '100%'
        };
    }
};

const getMapShapeStyles = (style) => {
    // Use circular shape for star map
    return {
        borderRadius: '50%' // Using 50% for a circular star map
    };
};

// Get wrapper style adjustments based on the orbital style
const getWrapperStyleAdjustments = (style) => {
    // Handle null/undefined styles
    if (!style) return {};

    // Get normalized style name
    const normalizedStyle = mapStyleName(style);

    // Return style adjustments based on the design
    switch (normalizedStyle) {
        case 'stellar simplicity':
            return { padding: '24px' };
        case 'orbit':
            return { padding: '24px' };
        case 'constellation frame':
            return { padding: '24px' };
        case 'celestial corners':
            return { padding: '36px' };
        case 'starlit halo':
            return { padding: '22px' };
        case 'lunar harmony':
            return { padding: '18px' };
        case 'astro mechanics':
            return { padding: '26px' };
        case 'cosmic crown':
            return { padding: '0' };
        default:
            return { padding: '24px' };
    }
};

const MapPreview = ({
    formData,
    cityName,
    isGenerating,
    loadingProgress,
    mapGenerated,
    mapError,
    handleStarMapError,
    handleStarMapLoaded,
    shouldRegenerateMap,
    starMapRef
}) => {
    // Lazy loading state
    const [isVisible, setIsVisible] = useState(false);
    const [shouldLoadMap, setShouldLoadMap] = useState(false);
    const containerRef = useRef(null);

    // Intersection Observer for lazy loading
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    // Delay map loading slightly to ensure container is ready
                    setTimeout(() => setShouldLoadMap(true), 100);
                }
            },
            { threshold: 0.1 } // Load when 10% visible
        );

        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => {
            if (containerRef.current) {
                observer.unobserve(containerRef.current);
            }
        };
    }, []);

    // Debug log for formData.orbitalStyle (reduced to prevent spam)
    // console.log('MapPreview - orbitalStyle:', formData.orbitalStyle);

    // Memoize preview values to prevent unnecessary re-renders
    const previewDate = useMemo(() => {
        console.log('🎯 DEBUG: MapPreview previewDate computation', {
            formDataDate: formData.date,
            formDataDateType: typeof formData.date,
            formDataDateTruthy: !!formData.date,
            timestamp: new Date().toISOString()
        });

        if (formData.date) {
            console.log('🎯 DEBUG: Using formData.date:', formData.date);
            return formData.date;
        }
        // Use today's date as default for preview - memoized to prevent re-renders
        const today = new Date();
        const todayFormatted = today.toISOString().split('T')[0];
        console.log('🎯 DEBUG: Using default today date:', todayFormatted);
        return todayFormatted; // Format as YYYY-MM-DD
    }, [formData.date]);

    const previewLocation = useMemo(() => {
        console.log('🗺️ DEBUG: MapPreview computing previewLocation', {
            formDataLocation: formData.location,
            formDataLocationType: typeof formData.location,
            formDataLocationLength: formData.location ? formData.location.length : 0,
            trimmed: formData.location ? formData.location.trim() : '',
            includesComma: formData.location ? formData.location.includes(',') : false
        });

        // Check if we have a valid location string with actual coordinates
        if (formData.location && formData.location.trim() && formData.location.includes(',')) {
            // Validate that the coordinates are actually numbers
            const coords = formData.location.split(',');
            const lat = parseFloat(coords[0]);
            const lng = parseFloat(coords[1]);

            console.log('🗺️ DEBUG: Location validation', {
                coords,
                lat,
                lng,
                latIsNaN: isNaN(lat),
                lngIsNaN: isNaN(lng),
                bothValid: !isNaN(lat) && !isNaN(lng)
            });

            if (!isNaN(lat) && !isNaN(lng)) {
                console.log('🗺️ DEBUG: Using provided location:', formData.location);
                return formData.location;
            }
        }

        // Use London coordinates as default for preview when location is invalid or empty
        console.log('🗺️ DEBUG: Using default London coordinates');
        return '51.5074,-0.1278'; // London, UK coordinates
    }, [formData.location]);

    const previewCaption = useMemo(() => {
        if (formData.caption) {
            return formData.caption;
        }
        // Use default caption for preview
        return 'Your Special Moment';
    }, [formData.caption]);

    // Memoize style calculations to prevent object recreation on every render
    const backgroundStyle = useMemo(() => getBackgroundStyle(formData.background), [formData.background]);
    const mapShapeStyles = useMemo(() => getMapShapeStyles(formData.orbitalStyle), [formData.orbitalStyle]);
    const wrapperStyleAdjustments = useMemo(() => getWrapperStyleAdjustments(formData.orbitalStyle), [formData.orbitalStyle]);

    // Add CSS to hide zoom controls
    useEffect(() => {
        // Add a style to hide zoom controls and fix SVG object positioning
        const style = document.createElement('style');
        style.textContent = `
            #celestial-zoomin, #celestial-zoomout {
                display: none !important;
                visibility: hidden !important;
            }
            
            /* Ensure SVG objects properly fit their containers */
            object[type="image/svg+xml"] {
                width: 100%;
                height: 100%;
                display: block;
            }
            
            /* Background container styling - positioning managed via inline styles */
            .background-container {
                /* Background properties are now managed via inline styles to avoid conflicts */
            }
            
            /* Ensure proper rendering of orbital decorations */
            .orbital-decoration-wrapper {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                z-index: 100;
                pointer-events: none;
                display: flex;
                align-items: center;
                justify-content: center;
                overflow: visible;
                padding: 24px;
            }
            
            /* Ensure the SVG inside scales properly */
            .orbital-decoration-wrapper object {
                width: 100%;
                height: 100%;
                display: block;
                object-fit: contain;
                max-width: 100%;
                max-height: 100%;
            }

            /* Ensure SVG objects properly fit their containers */
            .orbital-decoration-wrapper img {
                width: 100%;
                height: 100%;
                display: block;
                object-fit: cover;
                max-width: 100%;
                max-height: 100%;
                image-rendering: auto;
                filter: drop-shadow(0 0 2px rgba(255,255,255,0.1));
            }
            
            /* Ensure decorations and background elements are visible for high-quality capture */
            .poster-bg-container {
                position: relative;
                z-index: 1;
                transform: translateZ(0);
                -webkit-transform: translateZ(0);
                overflow: hidden;
            }
            
            /* Force proper rendering of SVG decorations */
            .orbital-decoration-wrapper svg,
            .orbital-decoration-wrapper img {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                transform: translateZ(0);
                -webkit-transform: translateZ(0);
            }
        `;
        document.head.appendChild(style);

        return () => {
            document.head.removeChild(style);
        };
    }, []);

    // Add a useEffect to set regenerate flag when preview values change
    useEffect(() => {
        console.log('🔄 DEBUG: MapPreview preview values changed', {
            previewLocation,
            previewDate,
            isGenerating,
            mapGenerated,
            formDataLocation: formData.location,
            formDataDate: formData.date
        });

        // Always regenerate map when either date or location changes
        // This ensures astronomical accuracy for any date/location combination
        if (formData.date && formData.date.trim()) {
            // User has provided a date - always regenerate to show correct astronomical positions
            shouldRegenerateMap.current = true;
            console.log('🔄 DEBUG: MapPreview set shouldRegenerateMap flag to true (date change)');
        } else if (formData.location && formData.location.trim() && formData.location.includes(',')) {
            // User has provided a valid location - regenerate for location changes
            shouldRegenerateMap.current = true;
            console.log('🔄 DEBUG: MapPreview set shouldRegenerateMap flag to true (location change)');
        }

        // Don't directly set isGenerating here - let the parent component handle loading state
        // The parent will handle proper animation timing
    }, [previewLocation, previewDate, formData.location, formData.date]); // Added formData dependencies for better change detection

    // NEW: Test function for complete preview SVG extraction
    const testSVGExtraction = async () => {
        console.log('🧪 Testing complete preview SVG extraction...');

        // First check if the star map is ready
        if (!mapGenerated || isGenerating) {
            console.warn('⚠️ Star map is not ready yet. Please wait for generation to complete.');
            alert('Please wait for the star map to finish generating before testing SVG extraction.');
            return;
        }

        if (starMapRef.current && starMapRef.current.extractCompleteMapPreview) {
            try {
                console.log('⏳ Starting complete preview extraction (this may take a few seconds)...');
                console.log('🎨 This will capture the entire preview including orbital decorations, background, and text.');

                // Give the page time to fully render all elements
                await new Promise(resolve => setTimeout(resolve, 2000));

                const extractedSVG = await starMapRef.current.extractCompleteMapPreview(formData.background);

                if (extractedSVG) {
                    console.log('✅ Complete preview extraction successful!');

                    console.log('📊 Complete SVG details:', {
                        width: extractedSVG.getAttribute('width'),
                        height: extractedSVG.getAttribute('height'),
                        viewBox: extractedSVG.getAttribute('viewBox'),
                        children: extractedSVG.children.length,
                        hasBackground: extractedSVG.querySelector('rect') ? 'Yes' : 'No',
                        hasStarMap: extractedSVG.querySelector('g') ? 'Yes' : 'No',
                        hasText: extractedSVG.querySelector('text') ? 'Yes' : 'No',
                        hasDecorations: extractedSVG.querySelector('image') ? 'Yes' : 'No'
                    });

                    // Get SVG as string for download
                    const svgString = starMapRef.current.getSVGString(extractedSVG);
                    if (svgString) {
                        console.log('📝 Complete SVG string length:', svgString.length, 'characters');

                        // Create a temporary download link to save the complete SVG
                        const blob = new Blob([svgString], { type: 'image/svg+xml' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = 'complete-starmap-preview.svg';
                        a.style.display = 'none';
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(url);

                        console.log('💾 Complete preview SVG downloaded successfully');
                        alert('✅ Complete preview SVG extracted successfully!\n\n🎨 Includes: orbital decorations, background, star map, and text\n📁 Check your downloads folder for complete-starmap-preview.svg');
                    } else {
                        throw new Error('Failed to serialize complete SVG to string');
                    }
                } else {
                    console.error('❌ Complete preview extraction failed');
                    alert('Complete preview extraction failed. Check the console for details.');
                }
            } catch (error) {
                console.error('❌ Complete preview extraction error:', error);
                alert(`Complete preview extraction failed: ${error.message}`);
            }
        } else {
            console.error('❌ Complete preview extraction not available');
            alert('Complete preview extraction function is not available. Please ensure the star map is loaded.');
        }
    };

    // NEW: Test function for jsPDF PDF generation
    const testPDFGeneration = async () => {
        console.log('🧪 Testing jsPDF PDF generation...');

        // First check if the star map is ready
        if (!mapGenerated || isGenerating) {
            console.warn('⚠️ Star map is not ready yet. Please wait for generation to complete.');
            alert('Please wait for the star map to finish generating before testing PDF generation.');
            return;
        }

        if (starMapRef.current && starMapRef.current.generatePDFWithJsPDF) {
            try {
                console.log('⏳ Starting jsPDF generation (this may take a few seconds)...');
                console.log('📄 This will generate a high-quality PDF using SVG extraction and jsPDF.');

                // Give the page time to fully render all elements
                await new Promise(resolve => setTimeout(resolve, 2000));

                const pdfBlob = await starMapRef.current.generatePDFWithJsPDF(formData.background);

                if (pdfBlob) {
                    console.log('✅ jsPDF generation successful!');
                    console.log(`📦 PDF size: ${(pdfBlob.size / 1024 / 1024).toFixed(2)} MB`);

                    // Create a temporary download link to save the PDF
                    const url = URL.createObjectURL(pdfBlob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'starmap-jspdf-test.pdf';
                    a.style.display = 'none';
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);

                    console.log('💾 jsPDF generated and downloaded successfully');
                    alert(`✅ jsPDF PDF generated successfully!\n\n📄 High-quality PDF generated using SVG extraction\n📦 Size: ${(pdfBlob.size / 1024 / 1024).toFixed(2)} MB\n📁 Check your downloads folder for starmap-jspdf-test.pdf`);
                } else {
                    console.error('❌ jsPDF generation failed');
                    alert('jsPDF generation failed. Check the console for details.');
                }
            } catch (error) {
                console.error('❌ jsPDF generation error:', error);
                alert(`jsPDF generation failed: ${error.message}`);
            }
        } else {
            console.error('❌ jsPDF generation not available');
            alert('jsPDF generation function is not available. Please ensure the star map is loaded.');
        }
    };

    // NEW: Test function for complete high-resolution PNG generation
    const testPNGGeneration = async () => {
        console.log('🧪 Testing complete high-resolution PNG generation...');

        // First check if the star map is ready
        if (!mapGenerated || isGenerating) {
            console.warn('⚠️ Star map is not ready yet. Please wait for generation to complete.');
            alert('Please wait for the star map to finish generating before testing PNG generation.');
            return;
        }

        if (starMapRef.current && starMapRef.current.generateHighResPNG) {
            try {
                console.log('⏳ Starting complete high-resolution PNG generation (this may take a few seconds)...');
                console.log('🎨 This will generate a 300 DPI PNG including background, decorations, star map, and text.');

                // Give the page time to fully render all elements
                await new Promise(resolve => setTimeout(resolve, 2000));

                // Generate 300 DPI complete PNG for print quality with background info
                const pngBlob = await starMapRef.current.generateHighResPNG(300, formData.background);

                if (pngBlob) {
                    console.log('✅ Complete high-resolution PNG generation successful!');
                    console.log(`📦 PNG size: ${(pngBlob.size / 1024 / 1024).toFixed(2)} MB`);

                    // Create a temporary download link to save the PNG
                    const url = URL.createObjectURL(pngBlob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'complete-starmap-300dpi-test.png';
                    a.style.display = 'none';
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);

                    console.log('💾 Complete high-resolution PNG generated and downloaded successfully');
                    alert(`✅ Complete high-resolution PNG generated successfully!\n\n🎨 Includes: Background, orbital decorations, star map, and text\n🖼️ 300 DPI perfect for professional printing\n📦 Size: ${(pngBlob.size / 1024 / 1024).toFixed(2)} MB\n📁 Check your downloads folder for complete-starmap-300dpi-test.png`);
                } else {
                    console.error('❌ Complete PNG generation failed');
                    alert('Complete PNG generation failed. Check the console for details.');
                }
            } catch (error) {
                console.error('❌ Complete PNG generation error:', error);
                alert(`Complete PNG generation failed: ${error.message}`);
            }
        } else {
            console.error('❌ PNG generation not available');
            alert('PNG generation function is not available. Please ensure the star map is loaded.');
        }
    };



    return (
        <div ref={containerRef} className="bg-black/30 rounded-lg overflow-hidden !w-full" style={{ paddingBottom: '30px' }}>
            {/* SVG definitions for clip paths */}
            <svg width="0" height="0" style={{ position: 'absolute' }}>
                <defs>
                    <clipPath id="heartClip">
                        <path d="M100,177 C 60,127 0,92 0,42 C 0,17 18,7 35,7 C 67,7 100,37 100,37 C 100,37 133,7 165,7 C 182,7 200,17 200,42 C 200,92 140,127 100,177 Z" />
                    </clipPath>
                </defs>
            </svg>

            {/* Framed poster preview */}
            <div className="relative w-full">
                {/* Frame */}
                <div className="relative bg-black shadow-xl rounded-lg aspect-[3/4]">
                    {/* Poster with mat/border */}
                    <div className="relative poster-bg-container bg-white h-full w-full rounded p-4 sm:p-6" style={{ backgroundColor: "white" }}>
                        {/* Decorative orbital elements - positioned as absolute overlay inside the poster container */}
                        <div
                            className="orbital-decoration-wrapper"
                            style={wrapperStyleAdjustments}
                        >
                            <OrbitalDecorations style={formData.orbitalStyle} />
                        </div>

                        {/* Poster background with map */}
                        <div
                            className="relative h-full w-full overflow-hidden rounded background-container"
                            style={{
                                ...backgroundStyle,
                                backgroundSize: '100% auto',
                                backgroundPosition: 'center center',
                                backgroundRepeat: 'no-repeat',
                            }}
                        >
                            {/* Map container positioned near the top */}
                            <div
                                className="circular-map absolute left-1/2 transform -translate-x-1/2"
                                style={{
                                    top: '85px',
                                    width: '80%',
                                    height: 'auto',
                                }}
                            >
                                <div
                                    className="relative aspect-square overflow-hidden" /* Using aspect-square for proper circular star map */
                                    style={{
                                        ...mapShapeStyles,
                                    }}
                                >
                                    {/* Loading indicator overlay (absolute positioned to avoid layout shifts) */}
                                    {isGenerating && (
                                        <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/20 backdrop-blur-[1px] rounded-full transition-all duration-500 ease-in-out">
                                            <div className="w-2/3 bg-black/40 rounded-lg px-4 py-3 shadow-lg transition-opacity duration-300">
                                                <div className="w-full h-2 bg-black/20 rounded-full overflow-hidden">
                                                    <div
                                                        className="h-full bg-gradient-to-r from-accent to-accent/80 transition-all duration-700 ease-in-out"
                                                        style={{ width: `${loadingProgress}%` }}
                                                    ></div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Actual star map content */}
                                    <div
                                        className="absolute overflow-hidden"
                                        style={{
                                            ...mapShapeStyles,
                                            zIndex: 1,
                                            top: 0,
                                            right: 0,
                                            bottom: 0,
                                            left: 0
                                        }}
                                    >
                                        {mapError ? (
                                            <div className="text-center py-8 px-4 text-red-400 h-full flex items-center justify-center">
                                                <p>Error generating star map: {mapError}</p>
                                            </div>
                                        ) : shouldLoadMap ? (
                                            (() => {
                                                console.log('🚀 DEBUG: About to render StarMapGenerator with props:', {
                                                    date: previewDate,
                                                    location: previewLocation,
                                                    regenerateFlag: shouldRegenerateMap.current,
                                                    formDataDate: formData.date,
                                                    formDataLocation: formData.location,
                                                    timestamp: new Date().toISOString()
                                                });

                                                return (
                                                    <StarMapGenerator
                                                        ref={starMapRef}
                                                        date={previewDate}
                                                        location={previewLocation}
                                                        caption={previewCaption}
                                                        mapStyle={formData.mapStyle}
                                                        size={800}
                                                        showStars={formData.showStars}
                                                        showConstellations={formData.showConstellations}
                                                        showConstellationNames={formData.showConstellationNames}
                                                        showGrid={formData.showGrid}
                                                        showDeepSkyObjects={formData.showDeepSkyObjects}
                                                        showEcliptic={formData.showEcliptic}
                                                        onError={handleStarMapError}
                                                        onLoaded={handleStarMapLoaded}
                                                        regenerate={shouldRegenerateMap.current}
                                                    />
                                                );
                                            })()
                                        ) : (
                                            <div className="h-full flex items-center justify-center text-white/60">
                                                <div className="text-center">
                                                    <div className="animate-pulse w-16 h-16 bg-white/10 rounded-full mx-auto mb-2"></div>
                                                    <p className="text-sm">Loading star map...</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Caption positioned at bottom with absolute positioning */}
                            <div
                                className="absolute w-full left-1/2 transform -translate-x-1/2"
                                style={{
                                    bottom: '60px',
                                    zIndex: 10,
                                }}
                            >
                                <MapCaption
                                    caption={previewCaption}
                                    location={previewLocation}
                                    date={previewDate}
                                    formData={formData}
                                    cityName={cityName}
                                />
                            </div>

                            {/* DEBUG: Test buttons - HIDDEN FOR PRODUCTION
                            <div
                                className="absolute right-2 top-2 z-20 space-y-1"
                            >
                                <button
                                    onClick={testSVGExtraction}
                                    className="block bg-blue-600 hover:bg-blue-700 text-white text-xs px-2 py-1 rounded shadow opacity-75 hover:opacity-100 transition-opacity"
                                    title="Extract complete preview as SVG (includes decorations, background, and text)"
                                >
                                    Extract SVG
                                </button>
                                <button
                                    onClick={testPNGGeneration}
                                    className="block bg-orange-600 hover:bg-orange-700 text-white text-xs px-2 py-1 rounded shadow opacity-75 hover:opacity-100 transition-opacity"
                                    title="Generate high-resolution PNG at 300 DPI (perfect for printing)"
                                >
                                    Generate PNG
                                </button>
                                <button
                                    onClick={testPDFGeneration}
                                    className="block bg-green-600 hover:bg-green-700 text-white text-xs px-2 py-1 rounded shadow opacity-75 hover:opacity-100 transition-opacity"
                                    title="Generate PDF using jsPDF and SVG extraction (high quality)"
                                >
                                    Generate PDF
                                </button>
                            </div>
                            */}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MapPreview; 